import React from 'react';
import { Check, Clock, X, Package, Truck, CreditCard, Edit3 } from 'lucide-react';
import { formatDateTime, formatRelativeTime } from '../../utils/formatRelativeTime';

const STATUS_LABELS = {
  pending: 'Aguardando pagamento',
  awaiting_approval: 'Aguardando aprovação',
  approved: 'Aprovado',
  paid: 'Pago',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
  rejected: 'Rejeitado',
  refunded: 'Reembolsado'
};

// Quando o backend devolve `order.statusEvents` (lista da tabela
// orderStatusEvents), preferimos usar essa lista — é a fonte verdadeira.
// Fallback nos campos *_At quando não houver eventos persistidos.
function buildEventsFromHistory(order) {
  const events = [{
    key: 'created',
    label: 'Pedido criado',
    at: order.createdAt,
    icon: Package
  }];
  for (const ev of order.statusEvents || []) {
    const label = `${STATUS_LABELS[ev.toStatus] || ev.toStatus}` +
      (ev.actor?.name ? ` · ${ev.actor.name}` : '') +
      (ev.note ? ` (${ev.note})` : '');
    let icon = Edit3;
    let variant = 'default';
    if (ev.toStatus === 'paid') icon = CreditCard;
    else if (ev.toStatus === 'shipped') icon = Truck;
    else if (ev.toStatus === 'delivered') { icon = Check; variant = 'success'; }
    else if (['cancelled', 'rejected'].includes(ev.toStatus)) { icon = X; variant = 'error'; }
    events.push({
      key: `event-${ev.id}`,
      label,
      at: ev.createdAt,
      icon,
      variant
    });
  }
  return events;
}

// Ordem cronológica dos eventos do pedido. Cada entry vem de um campo
// datetime no order (createdAt, approvedAt, paidAt, shippedAt, deliveredAt,
// rejectedAt). Eventos ainda não ocorridos aparecem em tom cinza.
function buildEvents(order) {
  if (!order) return [];
  if (Array.isArray(order.statusEvents) && order.statusEvents.length > 0) {
    return buildEventsFromHistory(order);
  }
  const events = [];

  events.push({
    key: 'created',
    label: 'Pedido criado',
    at: order.createdAt,
    icon: Package,
  });

  if (order.approvedAt) {
    events.push({
      key: 'approved',
      label: 'Aprovado',
      at: order.approvedAt,
      icon: Check,
    });
  } else if (order.status === 'awaiting_approval') {
    events.push({
      key: 'pending-approval',
      label: 'Aguardando aprovação',
      at: null,
      icon: Clock,
      pending: true,
    });
  }

  if (order.rejectedAt) {
    events.push({
      key: 'rejected',
      label: `Rejeitado${order.rejectionReason ? `: ${order.rejectionReason}` : ''}`,
      at: order.rejectedAt,
      icon: X,
      variant: 'error',
    });
  }

  if (order.paidAt) {
    events.push({
      key: 'paid',
      label: 'Pagamento confirmado',
      at: order.paidAt,
      icon: CreditCard,
    });
  }

  if (order.shippedAt) {
    events.push({
      key: 'shipped',
      label: order.trackingCode
        ? `Enviado · Código ${order.trackingCode}`
        : 'Enviado',
      at: order.shippedAt,
      icon: Truck,
    });
  }

  if (order.deliveredAt) {
    events.push({
      key: 'delivered',
      label: 'Entregue',
      at: order.deliveredAt,
      icon: Check,
      variant: 'success',
    });
  } else if (order.shippedAt) {
    events.push({
      key: 'pending-delivery',
      label: 'Em trânsito',
      at: null,
      icon: Truck,
      pending: true,
    });
  }

  return events;
}

const VARIANT_STYLES = {
  default: 'bg-primary text-white',
  success: 'bg-success text-white',
  error: 'bg-error text-white',
  pending: 'bg-app-surface-hover text-app-text-secondary',
};

const OrderTimeline = ({ order, className = '' }) => {
  const events = buildEvents(order);
  if (!events.length) return null;

  return (
    <ol className={`relative border-l-2 border-app-border pl-6 space-y-6 ${className}`}>
      {events.map((ev) => {
        const Icon = ev.icon;
        const styleKey = ev.pending ? 'pending' : ev.variant || 'default';
        return (
          <li key={ev.key} className="relative">
            <span
              className={`absolute -left-[2.1rem] top-0 w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white ${VARIANT_STYLES[styleKey]}`}
            >
              <Icon size={16} />
            </span>
            <div>
              <p className={`text-sm font-medium ${ev.pending ? 'text-app-text-secondary' : 'text-app-text'}`}>
                {ev.label}
              </p>
              {ev.at && (
                <p className="text-xs text-app-text-secondary mt-0.5">
                  {formatRelativeTime(ev.at)} · {formatDateTime(ev.at)}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default OrderTimeline;
